import { Checkbox, Form, message } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CheckOutlined } from '@ant-design/icons';
import orderApi from '../../apis/order';
import { ProductTypeOptions } from '../../constants/order';
import { ISize, ProductType } from '../../interfaces/Order';
import { GridContainer, PageContainer } from '../../styles/Global';
import { $background } from '../../styles/Variables';
import Button from '../Button/Button';
import CustomForm from '../Form/Form';
import CustomInput from '../Form/FormItem/Input/Input';
import {
  OrderInformationSection,
  StyledInputNumber,
  StyledSelect,
} from '../Order/Order.styled';
import PageHeader from '../PageHeader/PageHeader';

const ActionsStyled = styled.div`
  border-radius: 8px;
  background-color: ${$background};
  padding: 20px;
  width: 164px;
  position: sticky;
  top: 90px;
`;

const ActionsSyncStyled = styled(ActionsStyled)`
  top: 200px;

  .ant-btn-loading {
    &::before {
      display: none;
    }
  }
`;

const SizeDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm();

  const [sizeData, setSizeData] = useState<ISize>({
    size: '',
    description: '',
    price: 0,
    product: ProductType.sticker,
  });

  const fetchSize = async (id: string) => {
    try {
      const result = await orderApi.getSizeDetail(id);

      setSizeData(result);
      form.setFieldsValue({
        price: result.price,
        description: result.description,
        size: result.size,
        product: result.product,
        numericalOrder: result.numericalOrder,
        isOutOfStock: !!result.isOutOfStock,
      });
    } catch (error) {
      navigate(-1);
      console.error(error);
    }
  };

  useEffect(() => {
    if (!!id) {
      fetchSize(id);
    }
  }, []);

  const updateSize = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const {
        price,
        size,
        description,
        product,
        numericalOrder,
        isOutOfStock,
      } = form.getFieldsValue(true);

      const payload: ISize = {
        price,
        size,
        description,
        product,
        numericalOrder,
        isOutOfStock: !!isOutOfStock,
      };

      await orderApi.updateSize(id, payload);
      message.success('Update size success!');
      navigate(-1);
    } catch (e) {
      console.error(e);
      message.error('Update size failed');
    } finally {
      setLoading(false);
    }
  };

  const createSize = async () => {
    try {
      setLoading(true);
      const payload: ISize = form.getFieldsValue(true);

      await orderApi.createSize(payload);
      message.success('Create size success!');
      navigate(-1);
    } catch (e) {
      console.error(e);
      message.error('Create size failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!id) {
      createSize();
    } else {
      updateSize();
    }
  };

  return (
    <>
      <PageContainer>
        <PageHeader
          title="Size Detail"
          showBackButton
          customBlock={
            <Button
              name="size-detail-form"
              htmlType="submit"
              onClick={handleSubmit}
            >
              <CheckOutlined color="#fff" size={20} />
              Save
            </Button>
          }
        />
        <OrderInformationSection>
          <h3>Size Information</h3>
          <CustomForm
            name="size-detail-form"
            form={form}
            initialValues={sizeData}
            layout="vertical"
            scrollToFirstError
            requiredMark="optional"
            onFinish={() => {
              if (!id) {
                createSize();
                return;
              }

              updateSize();
            }}
          >
            <GridContainer className="m__b--18" $col={2} $gap={20}>
              <Form.Item
                name="size"
                label="Size Name"
                rules={[
                  {
                    required: true,
                    type: 'string',
                    message: 'Please input Size name',
                  },
                ]}
              >
                <CustomInput placeholder="Size name" />
              </Form.Item>
              <Form.Item
                name="price"
                label="Price"
                rules={[
                  {
                    required: true,
                    type: 'number',
                    message: 'Please input price',
                  },
                ]}
              >
                <StyledInputNumber
                  min={1}
                  max={1000000}
                  prefix="$"
                  className="w-100"
                />
              </Form.Item>
            </GridContainer>
            <Form.Item
              className="m__b--18"
              name="description"
              label="Description"
              rules={[
                {
                  type: 'string',
                },
                {
                  max: 255,
                  message: 'Description cannot be longer than 255 characters',
                },
              ]}
            >
              <CustomInput placeholder="Size description (Optional)" />
            </Form.Item>
            <GridContainer $col={2} $gap={20}>
              <Form.Item
                name="product"
                label="Product"
                rules={[
                  {
                    required: true,
                    type: 'string',
                    message: 'Please select product',
                  },
                ]}
              >
                <StyledSelect
                  className="w-100"
                  defaultValue={ProductType.sticker}
                  onChange={(value) => {
                    form.setFieldValue('product', value);
                  }}
                  options={ProductTypeOptions.filter(
                    (option) => option.value !== '',
                  )}
                />
              </Form.Item>
              <Form.Item name="numericalOrder" label="Numerical Order">
                <StyledInputNumber
                  min={1}
                  max={1000000}
                  placeholder="Numerical Order"
                  step={1}
                  className="w-100"
                />
              </Form.Item>
            </GridContainer>

            <Form.Item
              name="isOutOfStock"
              valuePropName="checked"
              className="m__t--18"
            >
              <Checkbox>Out of stock</Checkbox>
            </Form.Item>

            {/* <Form.Item>
              <ButtonGroupContainer>
                <Button htmlType="submit">Save</Button>
              </ButtonGroupContainer>
            </Form.Item> */}
          </CustomForm>
        </OrderInformationSection>

        {/* {!!id && (
          <div className="w-25 p__h--90">
            <ActionsStyled>
              <Button
                className="w-100 font-weight--300 bg--transparent border--none text-danger text-align--left p__v--4 p__h--11"
                icon={<DeleteOutlined className="font-size--16 p__r--8" />}
                type="text"
              >
                <span className="m__l--10 font-size--14">Remove</span>
              </Button>
            </ActionsStyled>
          </div>
        )} */}
      </PageContainer>
      {/* {showCancelConfirmationModal && (
        <BaseModal
          primaryBtn="Confirm"
          secondaryBtn="Cancel"
          // onPrimaryBtnClick={() => onCancel(true)}
          onSecondaryBtnClick={() => setShowCancelConfirmationModal(false)}
        >
          Quit editing? Changes you made so far will not be saved
        </BaseModal>
      )} */}
    </>
  );
};
export default SizeDetail;
